/**
 * Sale Report Table Configurations
 */

import React from 'react';

// Utility functions for formatting
const formatCurrency = (amount) => {
  if (!amount) return '-';
  return amount.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' });
};

const formatCategory = (category) => {
  return category ? category.toUpperCase() : '';
};

const formatCouponCode = (code) => {
  return code ? code.toUpperCase() : '';
};

// Default columns for sales data
export const D_COLUMNS = [
  {
    field: 'id',
    headerName: 'Product ID',
    width: 150,
    filterable: false,
    valueGetter: (params) => params.row.id,
  },
  {
    field: 'title',
    headerName: 'Title',
    width: 200,
    filterable: false,
    renderCell: (params) => {
      if (params.row.id === 'total')
        return <b>{params.row.category ? formatCategory(params.row.category) : ''}</b>;
    },
  },
  {
    field: 'category',
    headerName: 'Category',
    width: 100,
    filterable: false,
    renderCell: (params) => {
      if (params.row.id === 'total') return null;
      return formatCategory(params.row.category);
    },
  },
  {
    field: 'price',
    headerName: 'Price',
    width: 100,
    filterable: false,
    renderCell: (params) => {
      if (params.row.id === 'total') return null;
      return params.row.price ? formatCurrency(params.row.price) : '-';
    },
  },
  {
    field: 'original-stock',
    headerName: 'Total Stock',
    width: 100,
    filterable: false,
    valueGetter: (params) => {
      if (params.row.id === 'total') return null;
      return params.row.stock;
    },
  },
  {
    field: 'sold',
    headerName: 'Sold',
    width: 100,
    filterable: false,
    renderCell: (params) => {
      if (params.row.id === 'total') return <b>{params.row.sold}</b>;
      return params.row.sold ? params.row.sold : 0;
    },
  },
  {
    field: 'stock',
    headerName: 'Remaining',
    width: 100,
    filterable: false,
    renderCell: (params) => {
      if (params.row.id === 'total') return null;
      return params.row.stock - (params.row.sold ? params.row.sold : 0);
    },
  },
  {
    field: 'totalSale',
    headerName: 'Total Sale',
    width: 100,
    filterable: false,
    valueGetter: (params) => {
      let totalSale = params.row.totalSale
        ? params.row.totalSale
        : params.row.price * params.row.sold;
      if (params.row.id === 'total') totalSale = params.row.totalSale;

      return totalSale ? formatCurrency(totalSale) : '-';
    },
    renderCell: (params) => {
      if (params.row.id === 'total') return <b>{params.value}</b>;
      return params.value;
    },
  },
];

// Remaining data columns
export const R_COLUMNS = [
  {
    field: 'title',
    headerName: 'Title',
    width: 200,
    filterable: false,
    renderCell: (params) => {
      if (params.row.id === 'total')
        return <b>{params.row.category ? formatCategory(params.row.category) : ''}</b>;
    },
  },
  {
    field: 'category',
    headerName: 'Category',
    width: 100,
    filterable: false,
    renderCell: (params) => {
      if (params.row.id === 'total') return null;
      return formatCategory(params.row.category);
    },
  },
  {
    field: 'price',
    headerName: 'Remaining',
    width: 100,
    filterable: false,
    renderCell: (params) => {
      if (params.row.id === 'total') return null;
      if (
        params.row.sold &&
        params.row.sold != 0 &&
        params.row.price &&
        params.row.downPaymentAmount
      ) {
        let remainingAmount = params.row.price - params.row.downPaymentAmount;
        return formatCurrency(remainingAmount);
      } else {
        return '-';
      }
    },
  },
  {
    field: 'original-stock',
    headerName: 'Total Stock',
    width: 100,
    filterable: false,
    valueGetter: (params) => {
      if (params.row.id === 'total') return null;
      return params.row.stock;
    },
  },
  {
    field: 'sold',
    headerName: 'Sold',
    width: 100,
    filterable: false,
    renderCell: (params) => {
      if (params.row.id === 'total') return <b>{params.row.sold}</b>;
      return params.row.sold ? params.row.sold : 0;
    },
  },
  { field: 'stock', headerName: 'Remaining Stock', width: 100, filterable: false },
  {
    field: 'totalRemaining',
    headerName: 'Total Remaining',
    width: 100,
    filterable: false,
    valueGetter: (params) => {
      let remainingAmount = params.row.price - params.row.downPaymentAmount;
      let totalRemaining = remainingAmount * params.row.sold;
      if (params.row.id === 'total') totalRemaining = params.row.totalRemainingAmount;

      return totalRemaining ? formatCurrency(totalRemaining) : '-';
    },
    renderCell: (params) => {
      if (params.row.id === 'total') return <b>{params.value}</b>;
      return params.value;
    },
  },
];

// Coupon columns
export const C_COLUMNS = [
  {
    field: 'code',
    headerName: 'Coupon Code',
    width: 200,
    filterable: false,
    renderCell: (params) => {
      if (params.row.id === 'total') return <b>{formatCouponCode(params.row.code)}</b>;
      return formatCouponCode(params.row.code);
    },
  },
  {
    field: 'discount',
    headerName: 'Discount (TL)',
    width: 100,
    filterable: false,
    renderCell: (params) => {
      if (params.row.id === 'total') return null;
      return params.row.discount ? formatCurrency(params.row.discount) : '-';
    },
  },
  { field: 'stock', headerName: 'Stock', width: 100, filterable: false },
  {
    field: 'used',
    headerName: 'Used',
    width: 100,
    filterable: false,
    renderCell: (p) => {
      if (p.row.id === 'total') return <b>{p.row.totalUsed}</b>;
      return p.row.used ? p.row.used : 0;
    },
  },
  {
    field: 'remaining',
    headerName: 'Remaining',
    width: 100,
    filterable: false,
    renderCell: (p) => {
      if (p.row.id === 'total') return null;
      let remaining = p.row.stock - (p.row.used ? p.row.used : 0);
      if (remaining < 0) remaining = 0;
      return remaining;
    },
  },
  {
    field: 'totalDiscount',
    headerName: 'Total Discount (TL)',
    width: 150,
    filterable: false,
    renderCell: (p) => {
      if (p.row.id === 'total')
        return p.row.totalDiscount ? (
          <b>{formatCurrency(p.row.totalDiscount)}</b>
        ) : (
          '-'
        );
      let totalDisc = p.row.discount * (p.row.used ? p.row.used : 0);
      if (totalDisc == 0) return '-';
      else return formatCurrency(totalDisc);
    },
  },
];

// Constants
export const ORGANISATION_OR_VENUE_SALE_CHANNELS = ['vm', 'vp', 'ocm', 'op'];

// Export all column configurations
export const SALE_REPORT_TABLES = {
  DEFAULT: D_COLUMNS,
  REMAINING: R_COLUMNS,
  COUPONS: C_COLUMNS,
};
